import { useState } from 'react';
import {
  Box,
  Stack,
  Typography,
  Grid,
  IconButton,
  Drawer,
} from '@mui/material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { StorageKeys } from "../../../services/constants/StorageKeys";
import { storage } from "../../../services/storageService";
import SupplySummary from '../components/SupplySummary';
import Page from '../../../components/Page';
import Loading from '../../../components/Loading';
import usePagination from '../hooks/usePagination';
import CustomButton from '../../../components/CustomButton';
import { useNavigate } from "react-router-dom";
import { Routes } from "../../../routes/RouteConstants";
import usePreReserve from '../api/usePreReserve';
import Article from '../../../components/Article';
import { gradientTextSx, tint } from '../../../utils/textStyles';
import { isDarkTheme, isNeonTheme } from '../../../utils/themeTraits';
import useBookingSchedules from '../hooks/useBookingSchedules';
import { useTenant } from '../../../contexts/TenantContext';
import FuturisticDayColumn from '../components/FuturisticDayColumn';
import DefaultDayColumn from '../components/DefaultDayColumn';
import ChosenSupplyBand from '../components/ChosenSupplyBand';
import BookingTerms from '../components/BookingTerms';
import useProviderChoice from '../../provider/hooks/useProviderChoice';

const BookingLayout = () => {
  const { displayConfig } = useTenant();
  const isDark = isDarkTheme(displayConfig);
  const accent = displayConfig?.primaryColor;

  const selectedSupply = storage.get(StorageKeys.SELECTED_SUPPLY);
  const selectedAppointment = storage.get(StorageKeys.SELECTED_APPOINTMENT);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const navigate = useNavigate();
  const { providerId } = useProviderChoice();

  const {
    fetchedDailySchedules,
    loading,
    loadMoreSchedules,
    refreshSchedules,
  } = useBookingSchedules(selectedSupply?.id, providerId);

  // A slot the backend refused is gone from a fresh list: the one on screen is out of date.
  const { preReserve, loading: preReserving } = usePreReserve({ onSlotLost: refreshSchedules });

  const { currentPage, paginatedItems, handlePrevPage, handleNextPage } =
    usePagination(fetchedDailySchedules, loadMoreSchedules);

  const handleAppointmentClick = (date, weekday, time) => {
    storage.set(StorageKeys.SELECTED_APPOINTMENT, { date, weekday, time });
    setDrawerOpen(true);
  };

  const handleCancelAppointment = () => {
    storage.remove(StorageKeys.SELECTED_APPOINTMENT);
    setDrawerOpen(false);
  };

  const handleContinue = async () => {
    // Signed out, the guest comes back to the confirmation, which pre-reserves as it opens
    // (DECISIONS.md D26).
    if (!storage.get(StorageKeys.IS_USER_AUTHENTICATED)) {
      storage.set(StorageKeys.TARGET_ROUTE_BEFORE_REDIRECT, Routes.CONFIRM_BOOKING);
      navigate(Routes.LOGIN);
      return;
    }

    const appointment = storage.get(StorageKeys.SELECTED_APPOINTMENT);
    const preReservedBooking = await preReserve({
      supplyId: selectedSupply.id,
      date: appointment.date,
      startTime: appointment.time,
      providerId,
    });
    if (!preReservedBooking) {
      // Taken in the meantime (the snackbar says so), or on the way to sign in.
      setDrawerOpen(false);
      return;
    }

    // Whom the backend gave the slot to, „Bárki” included: the confirmation names them, and holds
    // the slot with them again (docs/MULTI_PROVIDER.md).
    storage.set(StorageKeys.SELECTED_APPOINTMENT, { ...appointment, providerId: preReservedBooking.providerId });
    navigate(Routes.CONFIRM_BOOKING);
  };

  const DayColumn = isDark ? FuturisticDayColumn : DefaultDayColumn;

  return (
    <Page>
      {/* A header band and the tenant's booking rules; the card's own padding would inset both
          from its edges (DECISIONS.md D31). */}
      <Article alignItems="stretch" sx={{ py: 0, px: 0, overflow: "hidden" }}>
        <ChosenSupplyBand supply={selectedSupply} />
        <BookingTerms />
      </Article>

      {loading && <Loading />}

      {!loading && fetchedDailySchedules.length > 0 && (
        <>
          <Box my={1} display="flex" justifyContent="center" alignItems="center" gap={2}>
            <IconButton
              onClick={handlePrevPage}
              disabled={currentPage === 1}
              disableRipple
              sx={isDark ? {
                color: accent,
                "&.Mui-disabled": { color: tint(accent, 0.37) },
              } : { "&:hover": { background: "none" } }}
            >
              <ArrowBackIosIcon />
              <Typography variant="body2" sx={{ ml: 0.5 }}>Előző</Typography>
            </IconButton>

            <IconButton
              onClick={handleNextPage}
              disableRipple
              sx={isDark ? { color: accent } : { "&:hover": { background: "none" } }}
            >
              <Typography variant="body2" sx={{ mr: 0.5 }}>Következő</Typography>
              <ArrowForwardIosIcon />
            </IconButton>
          </Box>
          <Grid container spacing={2}>
            {paginatedItems.map(dailySchedule => (
              <DayColumn
                key={dailySchedule.date}
                dailySchedule={dailySchedule}
                selectedAppointment={selectedAppointment}
                onAppointmentClick={handleAppointmentClick}
                displayConfig={displayConfig}
              />
            ))}
          </Grid>
        </>
      )}

      <Drawer
        anchor="bottom"
        open={drawerOpen}
        onClose={preReserving ? undefined : handleCancelAppointment}
        PaperProps={isNeonTheme(displayConfig) ? {
          sx: {
            background: "rgba(5, 10, 24, 0.95)",
            backdropFilter: "blur(12px)",
            borderTop: `1px solid ${tint(accent, 0.5)}`,
            boxShadow: `0 -4px 30px ${tint(accent, 0.2)}`,
          }
        } : isDark ? {
          sx: {
            background: "rgba(10, 10, 10, 0.96)",
            backdropFilter: "blur(12px)",
            borderTop: `1px solid ${tint(accent, 0.5)}`,
          }
        } : {}}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={3}
          p={3}
          justifyContent={{ sm: "space-evenly" }}
          alignItems="center"
        >
          <Stack direction="row" spacing={2} alignItems="center" justifyContent="center">
            <SupplySummary fixedWidth="200px" supply={selectedSupply} />
            {selectedAppointment &&
              <Stack>
                {/* Lit in the tenant's colour on the dark theme; plain ink on paper. */}
                <Typography fontWeight="bold" sx={isDark
                  ? gradientTextSx(displayConfig, displayConfig?.primaryColor || displayConfig?.color)
                  : { color: displayConfig?.color }}>
                  {selectedAppointment.date.replaceAll("-", ".")}
                </Typography>
                <Typography color={displayConfig?.color}>
                  {selectedAppointment.weekday}
                </Typography>
                <Typography color={displayConfig?.color}>
                  {selectedAppointment.time}
                </Typography>
              </Stack>
            }
          </Stack>
          <CustomButton text="Tovább" isLoading={preReserving} onClick={handleContinue} />
        </Stack>
      </Drawer>
    </Page>
  );
};

export default BookingLayout;